/**
 * Utilitários para datas "date-only" (YYYY-MM-DD) sem deslocamento de fuso horário.
 * `new Date('2026-06-01')` é interpretado como UTC e pode exibir 1 dia a menos no Brasil.
 */

/**
 * Converte string YYYY-MM-DD (ou ISO com hora) em Date no fuso local.
 * @param input - Data em YYYY-MM-DD ou ISO
 * @returns Date local ou null se inválida
 */
export function parseLocalDateYmd(input: string | undefined): Date | null {
  if (!input?.trim()) return null;
  const s = input.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, mo, da] = s.split('-').map(Number);
    const d = new Date(y, mo - 1, da);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (s.includes('T')) {
    return parseLocalDateYmd(s.split('T')[0]);
  }
  const parsed = new Date(s);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

/**
 * Formata data-only para exibição pt-BR (situações, faltas, atrasos, etc.).
 * @param dateStr - Data em YYYY-MM-DD ou ISO
 * @returns Texto localizado ou "—" se inválida
 */
export function formatLocalDate(dateStr: string): string {
  const parsed = parseLocalDateYmd(dateStr);
  if (!parsed) return '—';
  return parsed.toLocaleDateString('pt-BR');
}

/**
 * Timestamp para ordenação de strings date-only no fuso local.
 * @param dateStr - Data em YYYY-MM-DD ou ISO
 * @returns ms desde epoch ou 0 se inválida
 */
export function getLocalDateTimestamp(dateStr: string): number {
  return parseLocalDateYmd(dateStr)?.getTime() ?? 0;
}

/**
 * Converte Date para string YYYY-MM-DD no fuso local.
 * @param date - Instante de referência
 * @returns Data no formato aceito por input[type="date"]
 */
export function toLocalDateYmd(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/**
 * Formata data/hora ISO para exibição pt-BR (auditoria de registros).
 * @param isoStr - Timestamp ISO ou ausente
 * @returns Texto localizado ou "—" se inválido/ausente
 */
export function formatDateTime(isoStr: string | undefined): string {
  if (!isoStr?.trim()) return '—';
  const parsed = new Date(isoStr);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

/**
 * Verifica se a data informada é posterior ao dia de referência (início do dia local).
 * @param dateStr - Data em YYYY-MM-DD ou ISO
 * @param referenceDate - Dia usado como limite (padrão: hoje)
 * @returns true se dateStr for depois do dia de referência
 */
export function isDateAfterLocalDay(dateStr: string, referenceDate: Date = new Date()): boolean {
  const parsed = parseLocalDateYmd(dateStr);
  if (!parsed) return false;
  const refDay = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), referenceDate.getDate());
  return parsed.getTime() > refDay.getTime();
}
